import { useEffect, useState } from 'react';
import { usageApi, rupees } from '../lib/usageApi';
import type { ChannelUsage, UsageLogRow, UsageReport } from '../lib/usageApi';

/**
 * WHAT THIS SCHOOL HAS SENT, AND WHAT IT COST.
 *
 * ⚠ NOTHING IS BLOCKED, AND THE SCREEN SAYS SO IN WORDS.
 *
 * A page showing an allowance with a bar looks exactly like a page that enforces one, and a trust
 * that believes a limit is being enforced will stop watching it. The enforcement note is not small
 * print at the bottom; it is the second thing on the page.
 *
 * ⚠ A CHANNEL THAT CANNOT SEND SHOWS A SENTENCE, NOT A ZERO.
 *
 * There is no WhatsApp sender in this product, and SMS is held behind DLT registration. Both would
 * otherwise render "0 used" — which reads as "a quiet month" rather than "this does not work yet",
 * and that misreading is in exactly the direction that loses money quietly.
 *
 * ⚠ TRUST ADMIN ONLY. Not a security rule, a behavioural one: a teacher who can see how many SMS
 * are left starts rationing them, and the message they decide not to send is the one about a child
 * who did not arrive at school.
 */
/**
 * ⚠ THE ORDER OF THESE CARDS IS A JUDGEMENT, NOT THE ENUM.
 *
 * The server returns channels in `UsageChannel` order, which puts the two that cannot send
 * anything — SMS behind DLT, WhatsApp with no sender — in the middle of the row, and leaves the
 * only card carrying a rupee figure orphaned on a second line of the grid. The result reads as
 * "here are five things, one of them is an afterthought", and the afterthought is the bill.
 *
 * So: channels that cannot send at all go last, because they are a sentence rather than a figure;
 * anything that has actually cost money leads; the rest keep their order. A grid of five in four
 * columns always strands one card, and this makes sure the stranded one is the least important
 * rather than the most.
 */
function orderForReading(channels: ChannelUsage[]): ChannelUsage[] {
  const rank = (c: ChannelUsage) => (c.notWiredNote ? 2 : c.spentPaise > 0 ? 0 : 1);
  return [...channels].sort((a, b) => rank(a) - rank(b) || b.spentPaise - a.spentPaise);
}

export function UsagePage() {
  const [data, setData] = useState<UsageReport | null>(null);
  const [log, setLog] = useState<UsageLogRow[] | null>(null);
  const [channel, setChannel] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    usageApi.thisMonth()
      .then((d) => alive && (setData(d), setFailed(false)))
      .catch(() => alive && setFailed(true))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    usageApi.log(channel || undefined, 100)
      .then((r) => alive && setLog(r))
      .catch(() => alive && setLog([]));
    return () => { alive = false; };
  }, [channel]);

  if (loading && !data) return <p style={{ color: 'var(--ink-3)' }}>Adding it up…</p>;
  /**
   * ⚠ SHAPE, NOT NULLNESS — and this crashed before the check was written this way.
   *
   * The guard was `!data`. An endpoint that answers `{{}}` — a proxy, a stub, a bad deploy, a
   * gateway error page, a route that matched more broadly than intended — returns a TRUTHY object
   * with none of the fields this screen reads, so the guard passed and the render died on
   * `data.period.label`. Every page sweep run found this same fault on four different screens; it is not a
   * fixture artefact, it is what a bad morning looks like.
   *
   * Checking the one field the screen cannot work without turns a stack trace into an empty state.
   */
  if (failed || !data?.period || !Array.isArray(data.channels)) {
    return <div className="card" style={{ padding: 20, color: 'var(--ink-3)' }}>Could not load this.</div>;
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Account</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Messages and AI — {data.period.label}</h1>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4, maxWidth: '76ch' }}>
          Counted from a record of every send, not from a running total — so any figure here can be
          traced to the messages behind it.
        </p>
      </div>

      {/* ⚠ Second on the page, not last. */}
      <div className="card" style={{ padding: 14, marginBottom: 14, borderLeft: '3px solid var(--warn)' }}>
        <strong style={{ color: 'var(--warn-ink, var(--warn))' }}>Nothing is blocked.</strong>{' '}
        <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>{data.enforcementNote}</span>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
        gap: 12, marginBottom: 16,
      }}>
        {orderForReading(data.channels).map((c) => <ChannelCard key={c.channel} c={c} />)}
      </div>

      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
          <strong style={{ fontSize: 15 }}>Spent this month</strong>
          <span style={{ fontSize: 22, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
            {rupees(data.totalSpentPaise)}
          </span>
        </div>
        <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 6, marginBottom: 0, maxWidth: '80ch' }}>
          ⚠ Message rates are contracted per school and are not filled in yet, so this figure is AI
          only. Set your per-unit rates and the SMS and email columns start carrying their real cost.
        </p>
      </div>

      {data.warnings.length > 0 && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div className="viz-title" style={{ marginBottom: 8 }}>Worth knowing</div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {data.warnings.map((w, i) => (
              <li key={i} style={{ fontSize: 13.5, lineHeight: 1.6, marginBottom: 4 }}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="card" style={{ padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
          <div>
            <div className="viz-title">Every send, newest first</div>
            <p style={{ fontSize: 12, color: 'var(--ink-3)', margin: '4px 0 0' }}>
              This is the part that settles an argument about a bill. Recipients are shown masked.
            </p>
          </div>
          <select className="input" value={channel} onChange={(e) => setChannel(e.target.value)} style={{ minWidth: 150 }}>
            <option value="">Every channel</option>
            {data.channels.map((c) => (
              <option key={c.channel} value={String(c.channel)}>{c.label}</option>
            ))}
          </select>
        </div>

        {log === null && <p style={{ color: 'var(--ink-3)', fontSize: 13 }}>Loading…</p>}
        {log !== null && log.length === 0 && (
          <p style={{ color: 'var(--ink-3)', fontSize: 13, margin: 0 }}>
            Nothing recorded this month. The meter starts the first time something is sent after
            this is deployed — it cannot fill in the past.
          </p>
        )}

        {log !== null && log.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            {/* ⚠ minWidth on the table, not just overflowX on the wrapper. With width:100% and no
                floor the table obediently squeezes itself into a 390px phone: the Result column —
                the only reason anyone opens this log — falls off the right edge and the recipient
                is clipped mid-address, with no scrollbar to suggest anything is missing. A floor
                makes the container actually scroll. */}
            <table className="qb-table" style={{ fontSize: 12.5, width: '100%', minWidth: 680 }}>
              <thead>
                <tr>
                  <th>When</th><th>Channel</th><th>To</th><th>What triggered it</th>
                  <th className="num">Units</th><th className="num">Cost</th><th>Result</th>
                </tr>
              </thead>
              <tbody>
                {log.map((r, i) => (
                  <tr key={i}>
                    <td style={{ whiteSpace: 'nowrap' }}>{new Date(r.at).toLocaleString('en-IN')}</td>
                    <td>
                      {r.channel}
                      {/* ⚠ The encoding is on the row because it is the answer to "why did that
                          one notice cost three times the other one". */}
                      {r.encoding && <span style={{ color: 'var(--ink-3)' }}> · {r.encoding}</span>}
                      {r.model && <span style={{ color: 'var(--ink-3)' }}> · {r.model}</span>}
                    </td>
                    <td className="mono" style={{ color: 'var(--ink-3)' }}>{r.recipientHint ?? '—'}</td>
                    <td>{r.source ?? '—'}</td>
                    <td className="num">{r.quantity.toLocaleString('en-IN')}</td>
                    <td className="num">{r.costPaise > 0 ? rupees(r.costPaise) : '—'}</td>
                    <td className={r.outcome === 'Failed' ? 'tone-bad' : 'tone-ok'}>
                      {r.outcome}
                      {r.error && <span style={{ color: 'var(--ink-3)' }}> · {r.error}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function ChannelCard({ c }: { c: ChannelUsage }) {
  const tone = c.level === 2 ? 'var(--danger)' : c.level === 1 ? 'var(--warn)' : 'var(--ok)';
  const pct = Math.min(100, c.percentUsed ?? 0);

  return (
    <div className="card" style={{ padding: 16, opacity: c.notWiredNote ? 0.75 : 1 }}>
      <div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 4 }}>{c.label}</div>

      {/* ⚠ The sentence replaces the number. Showing both would let a reader take the zero and
          ignore the caveat, which is the whole failure this guards against. */}
      {c.notWiredNote ? (
        <p style={{ fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.55, margin: '6px 0 0' }}>
          {c.notWiredNote}
        </p>
      ) : (
        <>
          <div style={{ fontSize: 26, fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>
            {c.used.toLocaleString('en-IN')}
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            {c.unit}
            {c.spentPaise > 0 && <> · {rupees(c.spentPaise)}</>}
          </div>

          {c.percentUsed === null ? (
            <p style={{ fontSize: 11.5, color: 'var(--ink-3)', margin: '8px 0 0' }}>
              No allowance agreed — measured, not judged.
            </p>
          ) : (
            <>
              <div style={{ height: 6, background: 'var(--line-2)', borderRadius: 3, marginTop: 10, overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', background: tone }} />
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 5 }}>
                {c.percentUsed}% of {c.limitLabel}
              </div>
            </>
          )}

          {c.failed > 0 && (
            <div style={{ fontSize: 11.5, color: 'var(--danger)', marginTop: 5 }}>
              {c.failed.toLocaleString('en-IN')} of {c.events.toLocaleString('en-IN')} attempts failed
            </div>
          )}
        </>
      )}
    </div>
  );
}
